/**
 * Мелочи, которые иначе разъезжаются по разделам в трёх вариантах:
 * бейджи, суммы, даты, состояния загрузки.
 */

import type { ReactNode } from 'react'
import type { ContentSource, Currency } from '../api/types.js'

export function Badge({
  tone = 'muted',
  children,
}: {
  tone?: 'muted' | 'accent' | 'ok' | 'warn' | 'danger'
  children: ReactNode
}): JSX.Element {
  const suffix = tone === 'muted' ? '' : ` badge--${tone}`
  return <span className={`badge${suffix}`}>{children}</span>
}

/**
 * Владелец строки. Показывается везде, где строку можно править:
 * `seed` значит «ближайшая выкатка перезапишет это значение из файла»,
 * и знать об этом надо ДО правки, а не после.
 */
export function SourceBadge({ source }: { source: ContentSource }): JSX.Element {
  return source === 'admin' ? (
    <Badge tone="accent">admin</Badge>
  ) : (
    <Badge>from file</Badge>
  )
}

const CURRENCY_LABEL: Record<Currency, string> = {
  gold: '🪙 gold',
  gems: '💎 gems',
  energy: '⚡ energy',
}

export function currencyLabel(currency: Currency): string {
  return CURRENCY_LABEL[currency]
}

export function Money({ currency, value }: { currency: Currency; value: number }): JSX.Element {
  return (
    <span className={`amount cur-${currency}`}>
      {value.toLocaleString('ru-RU')}
    </span>
  )
}

/** Движение леджера: знак значим, поэтому он всегда явный. */
export function Delta({ value }: { value: number }): JSX.Element {
  const tone = value > 0 ? 'amount--plus' : 'amount--minus'
  return (
    <span className={`amount ${tone}`}>
      {value > 0 ? '+' : '−'}
      {Math.abs(value).toLocaleString('ru-RU')}
    </span>
  )
}

export function formatDate(iso: string | null): string {
  if (iso === null) return '—'
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return iso
  return date.toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export function shortId(id: string | null, length = 8): string {
  if (id === null) return '—'
  return id.length <= length ? id : `${id.slice(0, length)}…`
}

export function Notice({
  tone = 'muted',
  children,
}: {
  tone?: 'muted' | 'error' | 'ok' | 'warn'
  children: ReactNode
}): JSX.Element {
  const suffix = tone === 'muted' ? '' : ` notice--${tone}`
  return <div className={`notice${suffix}`}>{children}</div>
}

export function Stat({ label, children }: { label: string; children: ReactNode }): JSX.Element {
  return (
    <div className="stat">
      <span className="stat__label">{label}</span>
      <span className="stat__value">{children}</span>
    </div>
  )
}

/**
 * Карточка раздела. `padded={false}` — когда внутри таблица или журнал:
 * они рисуют свои отбивки сами, и внешний отступ дал бы полосу пустоты
 * вокруг сетки.
 */
export function Card({
  title,
  hint,
  actions,
  padded = true,
  children,
}: {
  title?: string
  hint?: ReactNode
  actions?: ReactNode
  padded?: boolean
  children: ReactNode
}): JSX.Element {
  const hasHead = title !== undefined
  // Шапка и внутренний отступ несовместимы: отступ у секции сдвинул бы
  // и разделительную линию шапки. Поэтому карточка с шапкой всегда flush,
  // а тело получает свой собственный `card__body`.
  const flush = hasHead || !padded

  return (
    <section className={flush ? 'card card--flush' : 'card'}>
      {hasHead && (
        <header className="card__head">
          <div>
            <h2 className="card__title">{title}</h2>
            {hint !== undefined && <p className="card__hint">{hint}</p>}
          </div>
          {actions !== undefined && <div className="row">{actions}</div>}
        </header>
      )}
      {padded && flush ? <div className="card__body">{children}</div> : children}
    </section>
  )
}
